import { useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon.tsx';
import { Button, Confirm, Skeleton } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, moneyIn, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

interface Doc {
  kind: 'invoice' | 'bill';
  id: string;
  name: string;
  partner: string;
  currency: string;
  fx_open: number;
  booked: number;
  revalued: number | null;
  gain: number | null;
}
interface Revaluation {
  date: string;
  docs: Doc[];
  currencies: { code: string; rate: number | null; rate_date: string | null; receivable: number; payable: number }[];
  missing: string[];
  gain: number;
  posted: { id: string; number: number } | null;
  history: { id: string; number: number; date: string; total: number; reversal: string | null }[];
}

const signed = (n: number) => (n === 0 ? '—' : money(n, { sign: true }));

/**
 * What customers and suppliers owe in other currencies, worth more or less at a day's rates than
 * when it was booked. Posting it (usually on the last day of a month) books the difference and
 * takes it back the next day, so payments later book the real gain or loss.
 */
export function RevaluationTab({ initialDate, version, onPosted, onOpenEntry }: { initialDate: string; version: number; onPosted: () => void; onOpenEntry: (id: string) => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const [date, setDate] = useState(initialDate > todayIso() ? todayIso() : initialDate);
  const [confirming, setConfirming] = useState(false);
  const [v, setV] = useState(0);
  const { data } = useAction<Revaluation>('accounting.revaluation', { date, v: version + v });
  const r = data && data.date === date ? data : null;
  const post = () =>
    void run(async () => {
      await action('accounting.post_revaluation', { date });
      setConfirming(false);
      setV((x) => x + 1);
      onPosted();
    }, t('reval.posted', { date: day(date) }));
  const invoices = r?.docs.filter((d) => d.kind === 'invoice') ?? [];
  const bills = r?.docs.filter((d) => d.kind === 'bill') ?? [];
  const table = (rows: Doc[], empty: string) => (
    <Table
      head={[t('reval.c_document'), t('reval.c_partner'), t('reval.c_open'), t('reval.c_booked'), t('reval.c_now'), t('reval.c_difference')]}
      phoneHide={[1, 3]}
      left={[1]}
      empty={empty}
      rows={rows.map((d) => ({
        key: d.id,
        cells: [
          <span className="tabular">{d.name}</span>,
          d.partner,
          <span className="tabular">{moneyIn(d.fx_open, d.currency)}</span>,
          <span className="tabular">{money(d.booked)}</span>,
          <span className="tabular">{d.revalued === null ? '—' : money(d.revalued)}</span>,
          <span className={`tabular strong ${d.gain && d.gain > 0 ? 'fx-gain' : d.gain && d.gain < 0 ? 'fx-loss' : ''}`}>{d.gain === null ? t('reval.no_rate') : signed(d.gain)}</span>,
        ],
      }))}
    />
  );

  return (
    <div className="rep-grid">
      <div className="toolbar rep-wide">
        <label className="vat-month">
          <span>{t('reval.date')}</span>
          <input className="input date-input" type="date" value={date} max={todayIso()} onChange={(e) => /^\d{4}-\d{2}-\d{2}$/.test(e.target.value) && setDate(e.target.value)} />
        </label>
        {r && can('accounting.post') && !r.posted && r.docs.length ? (
          <Button variant="primary" icon="check" onClick={() => setConfirming(true)} disabled={!!r.missing.length}>
            {t('reval.post')}
          </Button>
        ) : null}
      </div>
      {!r ? (
        <>
          <Skeleton h={112} r="var(--radius-lg)" />
          <Skeleton h={240} r="var(--radius-lg)" />
        </>
      ) : !r.docs.length ? (
        <section className="panel rep-wide">
          <div className="empty">
            <span className="empty-icon">
              <Icon name="globe" size={28} />
            </span>
            <p className="empty-title">{t('reval.none')}</p>
            <p className="empty-body">{t('reval.none_body')}</p>
            {can('company.rates') ? (
              <Link className="btn btn-secondary btn-md" to="/manage/currencies">
                <Icon name="globe" size={16} /> {t('reval.rates_link')}
              </Link>
            ) : null}
          </div>
        </section>
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('reval.title')}>
            <Kpi label={t('reval.k_receivable')} value={signed(r.currencies.reduce((s, c) => s + c.receivable, 0))} hint={t('reval.k_receivable_hint', { n: invoices.length })} />
            <Kpi label={t('reval.k_payable')} value={signed(r.currencies.reduce((s, c) => s + c.payable, 0))} hint={t('reval.k_payable_hint', { n: bills.length })} />
            <Kpi label={r.gain >= 0 ? t('reval.k_gain') : t('reval.k_loss')} value={money(Math.abs(r.gain))} hint={t('reval.k_hint')} />
          </section>
          {r.missing.length ? (
            <p className="acc-check rep-wide off" role="alert">
              <Icon name="alert" size={16} />
              {t('reval.missing', { codes: r.missing.join(', '), date: day(date) })}
            </p>
          ) : r.posted ? (
            <p className="acc-check rep-wide ok" role="status">
              <Icon name="checkCircle" size={16} />
              {t('reval.already', { date: day(date) })}
              <button className="link" onClick={() => onOpenEntry(r.posted!.id)}>
                {t('reval.open_entry', { n: r.posted.number })}
              </button>
            </p>
          ) : null}
          <section className="panel rep-wide" aria-labelledby="reval-rates">
            <div className="panel-head">
              <h2 id="reval-rates">{t('reval.rates')}</h2>
            </div>
            <div className="reval-rates">
              {r.currencies.map((c) => (
                <span key={c.code} className="fx-rate-line">
                  <span className="fx-chip">{c.code}</span>
                  {c.rate === null ? <span className="text-warn">{t('reval.no_rate')}</span> : <span className="tabular">{t('reval.rate_of', { rate: c.rate, date: day(c.rate_date!) })}</span>}
                </span>
              ))}
            </div>
          </section>
          <section className="panel rep-wide" aria-labelledby="reval-inv">
            <div className="panel-head">
              <h2 id="reval-inv">{t('reval.customers')}</h2>
            </div>
            {table(invoices, t('reval.no_invoices'))}
          </section>
          <section className="panel rep-wide" aria-labelledby="reval-bills">
            <div className="panel-head">
              <h2 id="reval-bills">{t('reval.suppliers')}</h2>
            </div>
            {table(bills, t('reval.no_bills'))}
          </section>
        </>
      )}
      {r?.history.length ? (
        <section className="panel rep-wide" aria-labelledby="reval-history">
          <div className="panel-head">
            <h2 id="reval-history">{t('reval.history')}</h2>
          </div>
          <Table
            head={[t('reval.date'), t('reval.c_entry'), t('reval.c_amount')]}
            rows={r.history.map((h) => ({
              key: h.id,
              onClick: () => onOpenEntry(h.id),
              cells: [<span className="tabular">{day(h.date)}</span>, <span className="tabular">#{h.number}</span>, <span className="tabular">{money(h.total)}</span>],
            }))}
          />
        </section>
      ) : null}
      {confirming && r ? (
        <Confirm
          title={t('reval.post')}
          body={t('reval.confirm', { date: day(date), amount: money(Math.abs(r.gain)), next: day(nextDay(date)) })}
          confirm={t('reval.post')}
          busy={busy}
          onClose={() => setConfirming(false)}
          onConfirm={post}
        />
      ) : null}
    </div>
  );
}

function nextDay(d: string) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + 1);
  return x.toISOString().slice(0, 10);
}
